import moment from "moment";
import mockReleaseData from "../data/mockData/releasesData";
import mockAccountingReportsData from "../data/mockData/accountingReportsData";
import {
  getAccountedEarningsKeys,
  getDemoAccountingReportRows,
} from "./demoAccountingSessionStore";

function mergeById(baseRows, extraRows) {
  const map = new Map();
  (baseRows || []).forEach(row => {
    if (row && row.id != null) {
      map.set(String(row.id), row);
    }
  });
  (extraRows || []).forEach(row => {
    if (row && row.id != null) {
      map.set(String(row.id), { ...(map.get(String(row.id)) || {}), ...row });
    }
  });
  return Array.from(map.values());
}

export function getAccountingReportRows() {
  return mergeById(mockAccountingReportsData, getDemoAccountingReportRows()).map(
    normalizeAccountingReportRow,
  );
}

function randomInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function shuffleArray(items) {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = randomInt(0, i);
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

function revenueSeed(releaseId, month, year) {
  const n = Number(releaseId) || 1;
  return Math.round((((n * 17 + month * 31 + year * 7) % 450) + 50) * 100) / 100;
}

function parsePeriodMonth(value, fallback) {
  if (!value || typeof value !== "string") {
    return fallback.clone();
  }
  const parsed = moment(value, "MM-YYYY", true);
  if (!parsed.isValid()) {
    return fallback.clone();
  }
  return parsed.startOf("month");
}

function formatEuro(amount) {
  return `${Number(amount).toFixed(2)} €`;
}

export function normalizeAccountingReportArtwork(art) {
  if (art == null) {
    return null;
  }
  if (typeof art === "string") {
    return {
      thumb_small: art,
      thumb_medium: art,
    };
  }
  if (typeof art === "object") {
    const thumbSmall = art.thumb_small || art.thumb_medium || null;
    const thumbMedium = art.thumb_medium || art.thumb_small || null;
    if (thumbSmall || thumbMedium) {
      return {
        thumb_small: thumbSmall || thumbMedium,
        thumb_medium: thumbMedium || thumbSmall,
      };
    }
  }
  return art;
}

export function normalizeAccountingReportRow(row) {
  if (!row || typeof row !== "object") {
    return row;
  }
  return {
    ...row,
    release_artwork: normalizeAccountingReportArtwork(row.release_artwork),
  };
}

export function resolveAccountingOverview(queryParams, accountedKeysInput) {
  const releaseId = String(queryParams?.get("release") || "0");
  const accountedKeys = accountedKeysInput || getAccountedEarningsKeys();

  const defaultEnd = moment().subtract(4, "month").startOf("month");
  const defaultStart = moment().subtract(15, "month").startOf("month");
  const start = parsePeriodMonth(queryParams?.get("period_start"), defaultStart);
  const endCandidate = parsePeriodMonth(queryParams?.get("period_end"), defaultEnd);
  const end = endCandidate.isBefore(start) ? start.clone() : endCandidate;

  const releases = mockReleaseData.map(release => ({
    id: release.id,
    name: release.name,
  }));

  if (!releaseId || releaseId === "0") {
    return { releases, revenue_data: [] };
  }

  const revenue_data = [];
  const monthsInRange = [];
  let cursor = start.clone();

  while (cursor.isSameOrBefore(end)) {
    monthsInRange.push({
      month: cursor.month() + 1,
      year: cursor.year(),
    });
    cursor.add(1, "month");
  }

  if (!monthsInRange.length) {
    return { releases, revenue_data };
  }

  const statementCount = randomInt(1, monthsInRange.length);
  const selectedMonths = shuffleArray(monthsInRange)
    .slice(0, statementCount)
    .sort((left, right) => {
      if (left.year !== right.year) {
        return left.year - right.year;
      }
      return left.month - right.month;
    });

  selectedMonths.forEach((entry, index) => {
    const key = `${releaseId}-${entry.year}-${entry.month}`;
    const accounted = accountedKeys.includes(key);
    revenue_data.push({
      id: index + 1,
      month: entry.month,
      year: entry.year,
      total_revenue: revenueSeed(releaseId, entry.month, entry.year),
      status: accounted ? 1 : 0,
    });
  });

  return { releases, revenue_data };
}

export function getNextAccountingReportId(rows) {
  return (rows || []).reduce((max, row) => {
    const val = Number(row?.id) || 0;
    return val > max ? val : max;
  }, 100) + 1;
}

export function buildAccountingReport({
  releaseId,
  releaseName,
  releaseArtwork,
  periodStart,
  periodEnd,
  revenueRows,
  nextId,
}) {
  const start = moment(periodStart).startOf("month");
  const end = moment(periodEnd).startOf("month");
  const period = `${start.format("MMM YYYY")} - ${end.format("MMM YYYY")}`;

  const openRows = (revenueRows || []).filter(row => row.status === 0);
  const totalRoyalties = openRows.reduce(
    (sum, row) => sum + (Number(row.total_revenue) || 0),
    0,
  );
  const costs = 0;
  const netRevenue = totalRoyalties - costs;
  const labelShare = netRevenue / 2;
  const shareholderShare = netRevenue / 2;
  const reportId = nextId;
  const itemId = reportId * 10 + 1;

  const accountedKeys = openRows.map(
    row => `${releaseId}-${row.year}-${row.month}`,
  );

  const report = {
    id: reportId,
    period,
    release: releaseName,
    release_artwork: normalizeAccountingReportArtwork(releaseArtwork),
    royalties: formatEuro(totalRoyalties),
    costs: formatEuro(costs),
    net_revenue: formatEuro(netRevenue),
    label_revenue: formatEuro(labelShare),
    shareholders_revenue: formatEuro(shareholderShare),
    status: 2,
    created_by: 12,
    date_created: new Date().toISOString(),
    report_pdf: "",
    accounting_report_items: [
      {
        id: itemId,
        accounting_report: reportId,
        track: releaseName || "Release",
        royalties: formatEuro(totalRoyalties),
        costs: formatEuro(costs),
        label_revenue: formatEuro(labelShare),
        shareholders_revenue: formatEuro(shareholderShare),
        net_revenue: formatEuro(netRevenue),
        store_wise_breakdown: {
          Spotify: (totalRoyalties * 0.42).toFixed(5),
          Deezer: (totalRoyalties * 0.12).toFixed(5),
          YouTube: (totalRoyalties * 0.18).toFixed(5),
        },
        territory_wise_breakdown: {
          Germany: (totalRoyalties * 0.28).toFixed(5),
          "United States of America": (totalRoyalties * 0.22).toFixed(5),
        },
        shareholder_breakdowns: [
          {
            id: itemId + 1,
            accounting_report_item: itemId,
            revenue_share_holder: "John Doe",
            revenue: formatEuro(shareholderShare),
          },
        ],
      },
    ],
    revenueshareholder_reports: [
      {
        id: reportId + 100,
        accounting_report: reportId,
        revenue_share_holder: "John Doe",
        email: "test@test.io",
        email_sent: false,
        revenue: formatEuro(shareholderShare),
        report_pdf: "",
      },
    ],
  };

  return { report, accountedKeys };
}

export function findAccountingReportById(id) {
  const rows = getAccountingReportRows();
  return rows.find(row => String(row.id) === String(id)) || null;
}

export function getReleaseById(releaseId) {
  return mockReleaseData.find(row => String(row.id) === String(releaseId)) || null;
}
